import { isActiveStatus } from './job-status';
import { MESSAGE_TYPE, MessageType } from './message-types';

type ContactablePerson = {
  email: string | null;
  phoneNumber: string | null;
  isPhoneNumberInvalid: boolean;
  firstName: string;
  lastName: string;
};

/*
THIS FILE ALSO EXISTS IN server/util/can-message.
The business logic should match between the two.
 */
export class PersonCanMessage {
  private _person: ContactablePerson;

  constructor(person: ContactablePerson) {
    this._person = person;
  }

  canReceive(type: MessageType) {
    if (type === MESSAGE_TYPE.EMAIL) {
      return this.canEmailMessage().canEmail;
    }
    if (type === MESSAGE_TYPE.TEXT) {
      return this.canTextMessage().canText;
    }
    throw new Error(`Unsupported type ${type}`);
  }

  canTextMessage(): CanTextMessageResult {
    if (!this._person.phoneNumber) {
      return {
        canText: false,
        errorMessage: this._person.email
          ? `${this._person.firstName} will receive messages as emails, rather than as texts.`
          : `We do not have a phone number for ${this._person.firstName}, please update in order to send texts.`,
      };
    }

    if (this._person.isPhoneNumberInvalid) {
      return {
        canText: false,
        errorMessage: `Phone number is either incorrect or is a number that does not accept texts. Please update in order to send texts to ${this._person.firstName}`,
      };
    }

    return {
      canText: true,
      errorMessage: null,
    };
  }

  canEmailMessage(): CanEmailMessageResult {
    if (!this._person.email) {
      return {
        canEmail: false,
        errorMessage: `there is no email available for ${this._person.firstName}`,
      };
    }

    return {
      canEmail: true,
      errorMessage: null,
    };
  }
}

export class ApplicantCanMessage extends PersonCanMessage {
  private _applicant: FullApplicant;

  private _job: Job | null;

  constructor(applicant: FullApplicant, job: Job | null) {
    super(applicant);

    this._applicant = applicant;
    this._job = job;
  }

  canTextMessage(): CanTextMessageResult {
    const personCanText = super.canTextMessage();
    if (!personCanText.canText) {
      return personCanText;
    }

    if (this._job && !this._job.fromPhoneNumber && !this._job.org.jobPhoneNumber) {
      return {
        canText: false,
        errorMessage: isActiveStatus(this._job.status)
          ? 'Text messaging is not enabled for this job. Please contact support@teamengine.io.'
          : 'Text messaging is not enabled for this job. Please activate the job to enable it.',
      };
    }

    if (this._applicant.optedOutOfTexts || this._applicant.isContactOptedOutOfApplicantTexting) {
      return {
        canText: false,
        errorMessage: `${this._applicant.firstName} has ${
          this._applicant.email
            ? 'opted to receive emails rather than texts'
            : 'opted out of receiving any more texts'
        }`,
      };
    }

    if (!this._applicant.textFollowups) {
      return {
        canText: false,
        errorMessage: `${this._applicant.firstName} has opted ${
          this._applicant.email ? 'to receive emails rather than texts' : 'not to receive texts'
        } about this job`,
      };
    }

    return {
      canText: true,
      errorMessage: null,
    };
  }
}

export class EmployeeCanMessage extends PersonCanMessage {
  private _employee: Employee;

  constructor(employee: Employee) {
    super(employee.Contact.Person);

    this._employee = employee;
  }

  canTextMessage(): CanTextMessageResult {
    const personCanText = super.canTextMessage();
    if (!personCanText.canText) {
      return personCanText;
    }

    if (this._employee.optedOutOfTexts) {
      return {
        canText: false,
        errorMessage: `${this._employee.Contact.Person.firstName}${
          this._employee.Contact.Person.email
            ? ' opted to receive emails rather than texts'
            : ` ${
                this._employee.manuallyOptedOutDate ? 'was' : 'has'
              } opted out of receiving any more texts`
        }`,
      };
    }

    return {
      canText: true,
      errorMessage: null,
    };
  }
}
